/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import {QueryParams} from './query-params';
import {AdditionalParams} from './AdditionalParams';
import {LazyLoadEvent} from "primeng/api";

export class UrlBuilderService {

    /***
     *
     * @param  baseUrl -  any string
     * @param  lazyLoadEvent - primeng LazyLoadEvent interface
     * @param  additionalParams - multiple key/value objects like:
     *        {test: 'true'} or {test1: 'true', test2: 'false'} or {test1: 'true'},{test2: 'false'} , where both: key and value are strings
     * @returns
     *
     * ------------------------------------------------------------------------------------------------------------------
     *
     * interface LazyLoadEvent {
     *       first?: number;
     *       rows?: number;
     *       sortField?: string;
     *       sortOrder?: number;
     *       multiSortMeta?: SortMeta[];
     *       filters?: {
     *           [s: string]: FilterMetadata;
     *       };
     *       globalFilter?: any;
     *   }
     *
     * ------------------------------------------------------------------------------------------------------------------
     * MatchMode:
     * enum FilterMatchMode {
     *       startsWith = "SW",
     *       contains = "LIKE",
     *       endsWith = "EW",
     *       equals = "EQ",
     *       in = "IN",
     *       lt = "LT",
     *       gt = "GT",
     * };
     *
     * ------------------------------------------------------------------------------------------------------------------
     *
     * Usage:
     *
     *  In component:
     *
     *  Init:
     *  constructor(private urlBuilderService:UrlBuilderService)
     *
     *  Call:
     *  this.urlBuilderService.createUrlFromLazyLoadEvent(
     *      'api/exceptions',
     *      {
     *          first: 2,
     *          rows: 10,
     *          sortField: 'createdBy',
     *          sortOrder: '1',
     *          filters: {
     *              createdAt: {value: '2017-11-08T00:00:00', matchMode: 'equals'}
     *          }
     *      },
     *      {test: 'true'})
     *
     *  Return: http://localhost:4200/api/exceptions?page=2&size=10&sort=createdBy,asc&filter[createdAt][EQ]=2017-11-08T00:00:00&test=true
     *
     * ------------------------------------------------------------------------------------------------------------------
     *  For filtering using the between values match Mode:
     *
     *  this.urlBuilderService.createUrlFromLazyLoadEvent(
     *      'api/exceptions',
     *      {
     *          filters: {
     *              createdAt: {value: {startDate: '2017-11-08T00:00:00', endDate: '2030-11-08T00:00:00'}, matchMode: undefined}
     *              age: {value: {min: '20', max: '60'}, matchMode: undefined}
     *          }
     *      })
     *
     *  http://localhost:4200/api/exceptions?filter[createdAt][GT]=2017-11-08T00:00:00&filter[createdAt][LT]=2030-11-08T00:00:00&[age][GT]=20&filter[age][LT]=60
     *
     * ------------------------------------------------------------------------------------------------------------------
     *
     */
    static createUrlFromLazyLoadEvent(baseUrl: string, lazyLoadEvent: LazyLoadEvent, ...additionalParams: Array<AdditionalParams>): string {

        const queryParams = new QueryParams(lazyLoadEvent, additionalParams);
        return `${baseUrl}${queryParams.toString()}`;

    }
}

